import React from 'react';
import { TextField, Select, Button, Flex, Text, type SelectOption } from '@chatsdet/ui';
import type { ApiSequenceValidation, ValidationOperator } from '@chatsdet/types';
import { Plus, Trash } from '@phosphor-icons/react';

const OPERATOR_OPTIONS: SelectOption[] = [
  { id: 'equals', label: 'equals' },
  { id: 'not_equals', label: 'not equals' },
  { id: 'gt', label: '>' },
  { id: 'lt', label: '<' },
  { id: 'gte', label: '>=' },
  { id: 'lte', label: '<=' },
  { id: 'contains', label: 'contains' },
  { id: 'not_contains', label: 'not contains' },
  { id: 'is_set', label: 'is set' },
  { id: 'is_not_set', label: 'is not set' },
];

const UNARY_OPERATORS = new Set<string>(['is_set', 'is_not_set']);

type StepValidationsEditorProps = {
  validations: ApiSequenceValidation[];
  onChange: (validations: ApiSequenceValidation[]) => void;
  availableStepIds: string[];
};

export function StepValidationsEditor({
  validations,
  onChange,
  availableStepIds,
}: StepValidationsEditorProps) {
  const addValidation = () => {
    onChange([
      ...validations,
      { left: '', operator: 'is_set' },
    ]);
  };

  const updateValidation = (index: number, patch: Partial<ApiSequenceValidation>) => {
    const updated = [...validations];
    updated[index] = { ...updated[index], ...patch };
    // Clear right value for unary operators
    if (patch.operator && UNARY_OPERATORS.has(patch.operator)) {
      updated[index].right = undefined;
    }
    onChange(updated);
  };

  const removeValidation = (index: number) => {
    onChange(validations.filter((_, i) => i !== index));
  };

  const stepHints = React.useMemo(
    () => availableStepIds.map((id) => `${id}.data.`).join(', '),
    [availableStepIds]
  );

  return (
    <div className="space-y-3">
      {validations.map((v, index) => (
        <div
          key={index}
          className="rounded-lg border border-border bg-card p-3 space-y-2"
        >
          <Flex align="center" justify="between">
            <Text size="xs" weight="medium" className="text-muted-foreground">
              Validation {index + 1}
            </Text>
            <Button
              variant="icon"
              size="sm"
              iconOnly
              tooltip="Remove validation"
              onPress={() => removeValidation(index)}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash size={14} />
            </Button>
          </Flex>

          <div className="flex items-end gap-2">
            <div className="flex-1">
              <TextField
                label="Value"
                aria-label="Left value reference"
                value={v.left}
                onChange={(value) => updateValidation(index, { left: value })}
                placeholder="step1.data.accountId"
                className="font-mono text-sm"
              />
            </div>
            <div className="w-36">
              <Select
                label="Operator"
                aria-label="Validation operator"
                items={OPERATOR_OPTIONS}
                selectedKey={v.operator}
                onSelectionChange={(key) =>
                  updateValidation(index, { operator: key as ValidationOperator })
                }
              />
            </div>
            {!UNARY_OPERATORS.has(v.operator) && (
              <div className="flex-1">
                <TextField
                  label="Expected"
                  aria-label="Expected value"
                  value={v.right ?? ''}
                  onChange={(value) => updateValidation(index, { right: value || undefined })}
                  placeholder="expected value"
                  className="font-mono text-sm"
                />
              </div>
            )}
          </div>
        </div>
      ))}

      <Button
        variant="outline"
        size="sm"
        onPress={addValidation}
        iconLeft={<Plus size={14} />}
      >
        Add Validation
      </Button>

      {availableStepIds.length > 0 && validations.length > 0 && (
        <Text size="xs" className="text-muted-foreground">
          Reference step values like: {stepHints}
        </Text>
      )}
    </div>
  );
}
